// ── QueueSheet — окно «Очередь»: что заиграет дальше, как в подкастах ─────────
// Сверху — что играет сейчас, ниже — очередь: перетаскивание за ручку (на
// компьютере ещё стрелки выше/ниже), нажатие на название — играть сразу,
// крестик — убрать. «Добавить» открывает список аудио с галочками: выбранные
// встают в конец очереди в том порядке, в каком их отметили.
// На телефоне — лист снизу, на компьютере — окно по центру.

import { useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import { SANS } from './themes';
import { Button, Confirm, IconButton, Sheet } from './ui';
import { nCards } from './utils';
import { useQueue, setQueue, addToQueue, removeFromQueue, clearQueue } from './queue';
import { t } from './i18n';

export default function QueueSheet({ C, projects, pickable, current, playing, onPlay, onClose }) {
  const queue = useQueue();
  const phone = window.innerWidth < 640;
  const byId = useMemo(() => new Map(projects.map((p) => [String(p.id), p])), [projects]);
  const items = queue.map((id) => byId.get(id)).filter(Boolean);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState([]); // порядок отметки = порядок в очереди
  const [filter, setFilter] = useState('');
  const [askClear, setAskClear] = useState(false);

  // ── Перетаскивание ──
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState(null); // временный порядок, пока тянем
  const orderRef = useRef(null);
  const rowRefs = useRef({});
  const list = order ? order.map((id) => byId.get(id)).filter(Boolean) : items;
  const ids = list.map((p) => String(p.id));
  const onGripDown = (e, id) => {
    e.preventDefault();
    orderRef.current = items.map((p) => String(p.id));
    setDragId(id); setOrder(orderRef.current);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* старый браузер */ }
  };
  const onGripMove = (e) => {
    const cur = orderRef.current;
    if (!dragId || !cur) return;
    const y = e.clientY;
    const rest = cur.filter((id) => id !== dragId);
    const to = rest.filter((id) => { const el = rowRefs.current[id]; if (!el) return false; const r = el.getBoundingClientRect(); return r.top + r.height / 2 < y; }).length;
    if (cur.indexOf(dragId) === to) return;
    rest.splice(to, 0, dragId);
    orderRef.current = rest;
    setOrder(rest);
  };
  const onGripUp = () => {
    const fin = orderRef.current;
    orderRef.current = null;
    setDragId(null); setOrder(null);
    if (fin) setQueue(fin);
  };
  const nudge = (id, d) => {
    const cur = items.map((p) => String(p.id));
    const i = cur.indexOf(id), j = i + d;
    if (i < 0 || j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    setQueue(cur);
  };

  // ── Выбор аудио ──
  const sortedPickable = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return pickable
      .filter((p) => !q || String(p.name || '').toLowerCase().includes(q) || String(p.folder || '').toLowerCase().includes(q))
      .sort((a, b) => String(a.folder || '').localeCompare(String(b.folder || ''), undefined, { numeric: true }) || String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true }));
  }, [pickable, filter]);
  const togglePick = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const startPicking = () => { setPicked([]); setFilter(''); setPicking(true); };
  const addPicked = () => { addToQueue(picked); setPicking(false); };

  const meta = (p) => [p.folder, nCards(p.cardCount || 0)].filter(Boolean).join(' · ');
  const label = { fontSize: 12.5, fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '.06em', padding: '12px 16px 6px' };
  const smallBtn = (primary) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, padding: '0 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: SANS, cursor: 'pointer',
    border: `1px solid ${primary ? C.blue : C.border}`, background: primary ? C.blue : 'transparent', color: primary ? '#fff' : C.text });
  const rowBtn = (disabled) => ({ width: 40, height: 40, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderRadius: 8,
    color: disabled ? C.border : C.mid, cursor: disabled ? 'default' : 'pointer', padding: 0 });
  const name = (p) => <span style={{ display: 'block', fontSize: 15, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>;
  const sub = (text) => <span style={{ display: 'block', fontSize: 12.5, color: C.faint, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>;

  const body = picking ? (
    <>
      {pickable.length > 8 && (
        <div style={{ padding: '4px 16px 8px' }}>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("Найти аудио")} aria-label={t("Найти аудио")}
            style={{ width: '100%', height: 40, boxSizing: 'border-box', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '0 12px', fontSize: 15, fontFamily: SANS, outline: 'none' }} />
        </div>
      )}
      {sortedPickable.length === 0 && <div style={{ padding: '24px 16px', textAlign: 'center', color: C.mid, fontSize: 14 }}>{pickable.length ? t("Ничего не найдено") : t("Нет аудио с загруженным звуком.")}</div>}
      {sortedPickable.map((p) => {
        const id = String(p.id);
        const n = picked.indexOf(id);
        const already = queue.includes(id);
        return (
          <label key={id} className="ac-row-click" style={{ '--ac-row-hover': C.surface, display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '4px 16px', cursor: 'pointer', borderTop: `1px solid ${C.borderSoft}` }}>
            <input type="checkbox" checked={n >= 0} onChange={() => togglePick(id)} style={{ width: 18, height: 18, accentColor: C.blue, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>{name(p)}{sub(already ? t("уже в очереди") : current && String(current.id) === id ? t("играет сейчас") : meta(p))}</span>
            {n >= 0 && <span aria-hidden="true" style={{ minWidth: 24, height: 24, borderRadius: 12, background: C.blue, color: '#fff', fontSize: 12.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>{n + 1}</span>}
          </label>
        );
      })}
    </>
  ) : (
    <>
      {current && (
        <>
          <div style={label}>{t("Сейчас")}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '4px 16px' }}>
            <span style={{ width: 36, height: 36, borderRadius: 8, background: C.bluePale, color: C.blueDark, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon n={playing ? 'volume' : 'pause'} s={16} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>{name(current)}{sub(playing ? t("играет") : t("на паузе"))}</span>
          </div>
        </>
      )}
      <div style={label}>{t("Далее")}</div>
      {list.length === 0 ? (
        <div style={{ padding: '8px 16px 20px', color: C.mid, fontSize: 14, lineHeight: 1.5 }}>
          {t("Очередь пуста. Добавьте аудио — они будут играть одно за другим, как выпуски подкаста.")}
          <div style={{ marginTop: 12 }}><button onClick={startPicking} style={smallBtn(true)}><Icon n="plus" s={15} />{t("Выбрать аудио")}</button></div>
        </div>
      ) : list.map((p, i) => {
        const id = String(p.id);
        return (
          <div key={id} ref={(el) => { rowRefs.current[id] = el; }}
            style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 52, padding: '2px 8px 2px 4px', borderTop: `1px solid ${C.borderSoft}`, background: dragId === id ? C.bluePale : 'transparent' }}>
            <span onPointerDown={(e) => onGripDown(e, id)} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}
              title={t("Перетащить")} aria-hidden="true" style={{ touchAction: 'none', cursor: dragId === id ? 'grabbing' : 'grab', color: C.faint, width: 36, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon n="grip" s={16} /></span>
            <span style={{ fontSize: 12.5, color: C.faint, width: 18, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{i + 1}</span>
            <button onClick={() => { removeFromQueue(id); onPlay(p); }} title={t("Играть сейчас")}
              style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', textAlign: 'left', padding: '6px 8px', cursor: 'pointer', fontFamily: SANS, borderRadius: 8 }}>
              {name(p)}{sub(meta(p))}
            </button>
            {!phone && <button className="ac-ibtn" onClick={() => nudge(id, -1)} disabled={i === 0} title={t("Выше")} aria-label={t("Выше")} style={rowBtn(i === 0)}><Icon n="chevronUp" s={16} /></button>}
            {!phone && <button className="ac-ibtn" onClick={() => nudge(id, 1)} disabled={i === ids.length - 1} title={t("Ниже")} aria-label={t("Ниже")} style={rowBtn(i === ids.length - 1)}><Icon n="chevronDown" s={16} /></button>}
            <button className="ac-ibtn" onClick={() => removeFromQueue(id)} title={t("Убрать из очереди")} aria-label={t("Убрать из очереди: {0}", [p.name])} style={rowBtn(false)}><Icon n="close" s={15} /></button>
          </div>
        );
      })}
    </>
  );

  const close = () => { if (picking) setPicking(false); else onClose(); };
  return (
    <Sheet C={C} size="sm" padded={false} onClose={close}
      title={<span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: picking ? -7 : 0 }}>
        {picking && <IconButton C={C} icon="arrowLeft" label={t("Назад")} variant="ghost" onClick={() => setPicking(false)} style={{ marginLeft: -8 }} />}
        <span style={{ minWidth: 0 }}>
          {picking ? t("Добавить в очередь") : t("Очередь")}
          {!picking && items.length > 0 && <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, color: C.faint }}>{t("Играет по порядку, одно за другим")}</span>}
        </span>
      </span>}
      headerExtra={!picking && items.length > 0 && <IconButton C={C} icon="plus" label={t("Добавить аудио")} onClick={startPicking} />}
      footer={(picking || items.length > 0) && (picking ? (
        <>
          <Button C={C} onClick={() => setPicking(false)}>{t("Отмена")}</Button>
          <Button C={C} variant="primary" icon="plus" onClick={addPicked} disabled={!picked.length}>
            {picked.length ? t("Добавить: {0}", [picked.length]) : t("Добавить")}
          </Button>
        </>
      ) : (
        <>
          <Button C={C} variant="ghost" danger icon="trash" onClick={() => setAskClear(true)} style={{ marginRight: 'auto' }}>{t("Очистить")}</Button>
          <Button C={C} icon="plus" onClick={startPicking}>{t("Добавить аудио")}</Button>
        </>
      ))}>
      {body}
      {askClear && <Confirm C={C} title={t("Очистить очередь?")} confirmLabel={t("Очистить")} onConfirm={clearQueue} onClose={() => setAskClear(false)} />}
    </Sheet>
  );
}
